"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import { Link } from "@/components/LocaleLink";
import { Clock, Calendar, ArrowRight, Search, X } from "lucide-react";
import type { BlogPostMeta } from "@/lib/blog";
import type { Locale } from "@/lib/locale";

interface BlogListProps {
  posts: BlogPostMeta[];
  locale: Locale;
  labels: {
    featuredSection: string;
    allSection: string;
    empty: string;
    noResults: string;
    resetFilters: string;
    searchPlaceholder: string;
    allCategories: string;
    readArrow: string;
    readTimeSuffix: string;
  };
}

function formatDate(dateStr: string, locale: Locale) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(locale === "en" ? "en-US" : "fr-FR", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export function BlogList({ posts, locale, labels }: BlogListProps) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<string | null>(null);

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return Array.from(set);
  }, [posts]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter((p) => {
      if (category && p.category !== category) return false;
      if (!q) return true;
      const haystack = [
        p.title,
        p.excerpt,
        p.category,
        ...(p.keywords ?? []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [posts, search, category]);

  const featured = filtered.filter((p) => p.featured);
  const recent = filtered.filter((p) => !p.featured);
  const hasFilters = search.length > 0 || category !== null;
  const isEmpty = posts.length === 0;
  const noMatches = !isEmpty && filtered.length === 0;

  if (isEmpty) {
    return (
      <section className="py-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-muted-foreground">{labels.empty}</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="py-8 border-b bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="mx-auto flex max-w-5xl flex-col gap-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={labels.searchPlaceholder}
                className="h-11 w-full rounded-full border bg-background pl-11 pr-10 text-sm outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label={labels.resetFilters}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {categories.length > 1 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCategory(null)}
                  className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    category === null
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                  }`}
                >
                  {labels.allCategories}
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setCategory(cat === category ? null : cat)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                      category === cat
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {noMatches ? (
        <section className="py-16">
          <div className="container mx-auto px-4 text-center">
            <p className="text-muted-foreground">{labels.noResults}</p>
            {hasFilters && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setCategory(null);
                }}
                className="mt-4 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
                {labels.resetFilters}
              </button>
            )}
          </div>
        </section>
      ) : (
        <>
          <section className="py-12">
            <div className="container mx-auto px-4">
              {featured.length > 0 && !hasFilters && (
                <h2 className="text-2xl font-bold mb-8">
                  {labels.featuredSection}
                </h2>
              )}
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {(hasFilters ? filtered : featured.length > 0 ? featured : filtered).map(
                  (post) => (
                    <Link
                      key={post.slug}
                      href={"/blog/" + post.slug}
                      className="group"
                    >
                      <article className="flex h-full flex-col overflow-hidden rounded-xl border bg-card transition-all hover:shadow-lg hover:border-primary/50">
                        <div className="aspect-video relative bg-gradient-to-br from-primary/20 to-primary/5 overflow-hidden">
                          {post.image ? (
                            <Image
                              src={post.image}
                              alt={post.title}
                              fill
                              className="object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-4xl opacity-50">📝</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-1 flex-col p-6">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
                              {post.category}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {post.readTime} {labels.readTimeSuffix}
                            </span>
                          </div>
                          <h3 className="text-xl font-semibold group-hover:text-primary transition-colors">
                            {post.title}
                          </h3>
                          <p className="mt-2 flex-1 text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                          <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3.5 w-3.5" />
                              {formatDate(post.date, locale)}
                            </span>
                            <span className="flex items-center gap-1 text-primary font-medium group-hover:gap-2 transition-all">
                              {labels.readArrow}{" "}
                              <ArrowRight className="h-4 w-4" />
                            </span>
                          </div>
                        </div>
                      </article>
                    </Link>
                  )
                )}
              </div>
            </div>
          </section>

          {!hasFilters && featured.length > 0 && recent.length > 0 && (
            <section className="py-12 bg-muted/30">
              <div className="container mx-auto px-4">
                <h2 className="text-2xl font-bold mb-8">{labels.allSection}</h2>
                <div className="grid gap-6 md:grid-cols-2">
                  {recent.map((post) => (
                    <Link
                      key={post.slug}
                      href={"/blog/" + post.slug}
                      className="group"
                    >
                      <article className="flex gap-4 rounded-xl border bg-card p-4 transition-all hover:shadow-md hover:border-primary/50">
                        <div className="h-24 w-24 flex-shrink-0 rounded-lg relative bg-gradient-to-br from-primary/20 to-primary/5 overflow-hidden">
                          {post.image ? (
                            <Image
                              src={post.image}
                              alt={post.title}
                              fill
                              className="object-cover"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center">
                              <span className="text-2xl opacity-50">📝</span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                              {post.category}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {post.readTime} {labels.readTimeSuffix}
                            </span>
                          </div>
                          <h3 className="font-semibold group-hover:text-primary transition-colors">
                            {post.title}
                          </h3>
                          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                            {post.excerpt}
                          </p>
                        </div>
                      </article>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </>
  );
}
